import { useEffect, useState, type JSX } from 'react';

import { Col, Row, Skeleton } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  cancelDeposit,
  cancelWithdraw,
  doClearOfferDetails,
  getOfferDetails,
  doCancelOfferOrder,
  doCancelOrder,
  doClearCancelOrder,
  doClearCancelOfferOrder,
  doClearCancelDeposit,
  doClearCancelWithdraw,
  doClearGetQuote,
  doClearGetQuoteChart,
  doClearUpdateOrder,
} from 'src/actions';
import { FilterBarConstant, NavigatePathConstant } from 'src/constants';
import { OfferOrderPaymentTypeDto } from 'src/dtos';
import { useSubmittedAccountSelector, useOfferOrders, useTransfers, useCumulativeOrders } from 'src/hooks';
import {
  CancelOfferOrderButton,
  CancelOrderButton,
  ChangeOrderButton,
  ChangeOrderModal,
  CancelTransferButton,
  ConfirmActionModal,
  LinkText,
  MTable,
  OfferOrderStatusTag,
  OfferOrderTypeCol,
  OrderStatusTag,
  OrderTypeCol,
  TransferStatusTag,
  TransferTypeCol,
  SecuritiesHeldAndFundedOutsideDisclaimerModal,
  ExternalOfferDisclaimerModal,
  CurrencyField,
  FilterBar,
  MTypography,
  ViewDetailsButton,
} from 'src/lib';
import {
  OfferDetails,
  OfferOrder,
  OfferOrderStatusLabel,
  Order,
  OrderStatusLabel,
  Transfer,
  TransferStatusLabel,
} from 'src/models';
import { Color, ScreenBreakpoint } from 'src/styles';
import {
  getOfferOrderStatusTagColor,
  getOrderStatusTagColor,
  mapFilterToGetTransferListQueryParamsDto,
  getTransferStatusTagColor,
  mapFilterToGetOrderListQueryParamsDto,
  onFixedTimelineFilterSelected,
} from 'src/utils';

import * as Styles from './History.styles';
import { isOrder, isTransfer } from './history.utils';
import { HistoryViewDetailsModal } from './HistoryViewDetailsModal';

interface HistoryViewModel {
  key: React.Key;
  date: string;
  type: JSX.Element;
  category: FilterBarConstant.Show;
  amount: JSX.Element;
  status: string;
  statusCategory: FilterBarConstant.StatusCategory;
  change: JSX.Element | null;
  cancel: JSX.Element | null;
  view: JSX.Element;
}

// Change/Cancel render nothing on rows that are not actionable, so their columns need a floor to
// stop the grid reflowing between pages. antd v4 has no column.minWidth, and a <col> width is only
// a preferred width that the auto table layout shrinks under space pressure, so the floor has to be
// set on the cells themselves. Sized to clear the widest button ('Change' at Font.lg semibold).
const ACTION_COLUMN_MIN_WIDTH = 112;

const historyListCols: ColumnsType<Omit<HistoryViewModel, 'status' | 'category' | 'statusCategory'>> = [
  {
    title: 'Date',
    dataIndex: 'date',
    width: 96,
    key: 'date',
    render: value => <LinkText value={value} testId={'column-date'} />,
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
  },
  {
    title: 'Action',
    key: 'change',
    dataIndex: 'change',
    width: 1,
    onCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
    onHeaderCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
  },
  {
    key: 'cancel',
    dataIndex: 'cancel',
    width: 1,
    onCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
    onHeaderCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
  },
  {
    key: 'view',
    dataIndex: 'view',
    width: 96,
  },
];

const defaultValues: FilterBarConstant.Sample = {
  sortBy: FilterBarConstant.SortBy.Newest,
  timeline: FilterBarConstant.FixedTimeline.ThisYear,
  customTimeline: undefined,
  filterBy: [],
  show: [],
};

const History = () => {
  const params = useLocation().search;
  const searchParams = new URLSearchParams(params);
  const filterBy = searchParams?.get('filterBy');
  const show = searchParams?.get('show');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [historyList, setHistoryList] = useState<HistoryViewModel[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<HistoryViewModel[]>([]);
  const [filters, setFilters] = useState<FilterBarConstant.Sample>({
    ...defaultValues,
    filterBy: filterBy ? [...JSON.parse(filterBy)] : defaultValues.filterBy,
    show: show ? [...JSON.parse(show)] : defaultValues.show,
  });

  const [isSecuritiesPurchasedByCardModalOpen, setIsSecuritiesPurchasedByCardModalOpen] = useState<boolean>(false);
  const [isExternalOfferDisclaimerOpen, setIsExternalOfferDisclaimerOpen] = useState<boolean>(false);

  const offerDetails: OfferDetails | undefined = useSelector((state: any) => state.offers.offerDetails?.data);
  const isOfferDetailsLoading: boolean = useSelector((state: any) => state.offers.offerDetails?.isLoading);

  const isCancelOrderLoading = useSelector((state: any) => state.orders.cancel.isLoading);
  const cancelOrderError = useSelector((state: any) => state.orders.cancel.error);
  const cancelOrderFailed = useSelector((state: any) => Boolean(state.orders.cancel.__failed));
  const cancelOrderSuccess = useSelector((state: any) => Boolean(state.orders.cancel.__succeeded));
  const isCancelOfferOrderLoading = useSelector((state: any) => state.offerOrders.cancel.isLoading);
  const cancelOfferOrderError = useSelector((state: any) => state.offerOrders.cancel.error);
  const cancelOfferOrderFailed = useSelector((state: any) => Boolean(state.offerOrders.cancel.__failed));
  const cancelOfferOrderSuccess = useSelector((state: any) => Boolean(state.offerOrders.cancel.__succeeded));
  const isCancelDepositLoading = useSelector((state: any) => state.cashiering.cancelDeposit.isLoading);
  const cancelDepositError = useSelector((state: any) => state.cashiering.cancelDeposit.error);
  const cancelDepositFailed = useSelector((state: any) => Boolean(state.cashiering.cancelDeposit.__failed));
  const cancelDepositSuccess = useSelector((state: any) => Boolean(state.cashiering.cancelDeposit.__succeeded));
  const isCancelWithdrawLoading = useSelector((state: any) => state.cashiering.cancelWithdraw.isLoading);
  const cancelWithdrawError = useSelector((state: any) => state.cashiering.cancelWithdraw.error);
  const cancelWithdrawFailed = useSelector((state: any) => Boolean(state.cashiering.cancelWithdraw.__failed));
  const cancelWithdrawSuccess = useSelector((state: any) => Boolean(state.cashiering.cancelWithdraw.__succeeded));

  const [isViewHistoryDetailsModalOpen, setIsViewHistoryDetailsModalOpen] = useState<boolean>(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<Transfer | OfferOrder | Order | null>(null);
  const [isCancelOrderModalOpen, setIsCancelOrderModalOpen] = useState<boolean>(false);
  const [isCancelOfferOrderModalOpen, setIsCancelOfferOrderModalOpen] = useState<boolean>(false);
  const [isCancelTransferModalOpen, setIsCancelTransferModalOpen] = useState<boolean>(false);
  const [selectedCancelOrder, setSelectedCancelOrder] = useState<Order | null>(null);
  const [selectedCancelOfferOrder, setSelectedCancelOfferOrder] = useState<OfferOrder | null>(null);
  const [selectedCancelTransfer, setSelectedCancelTransfer] = useState<Transfer | null>(null);
  const [selectedChangeOrder, setSelectedChangeOrder] = useState<Order | null>(null);

  const { account, isAccountLoading } = useSubmittedAccountSelector();
  const { offerOrderList, isOfferOrderListLoading } = useOfferOrders({
    dependency: [account.accountId],
    refetchOnChange: true,
  });
  // NOTE: we need to fetch all orders upfront rather than refetching on every filter change due to the cost (given that
  // on every filter change we would need to fetch all order pages to get the total list of orders)
  // refetchOnChange is added because in contrast to summary page which uses the hook, history page
  // will need all orders upfront othwerwise if the user updates the status filter after the initial fetch, it will not
  // be able to see the correct list of orders.
  const { cumulativeOrderList, isCumulativeOrderListLoading } = useCumulativeOrders({
    queryParamsDto: mapFilterToGetOrderListQueryParamsDto(filters, { skipFilterBy: true }),
    dependency: [filters.timeline, filters.customTimeline, account.accountId],
    refetchOnChange: true,
  });

  const { transferList, isTransferListLoading } = useTransfers({
    queryParams: mapFilterToGetTransferListQueryParamsDto(filters),
    dependency: [filters.timeline, filters.customTimeline, account.accountId],
    refetchOnChange: true,
  });

  const isMobile = useMediaQuery({ query: `(max-width: ${ScreenBreakpoint.mobile.max})` });

  const onViewMore = (offerOrder: OfferOrder) => {
    if (offerOrder.paymentType.isCard) {
      setIsSecuritiesPurchasedByCardModalOpen(true);

      return;
    }

    if (offerDetails?.id !== offerOrder.offerId) {
      dispatch(getOfferDetails(offerOrder.offerId));
    }
    setIsExternalOfferDisclaimerOpen(true);
  };

  const onCancel = (value: Transfer | OfferOrder | Order) => {
    if (isOrder(value)) {
      setSelectedCancelOrder(value);
      setIsCancelOrderModalOpen(true);

      return;
    }

    if (isTransfer(value)) {
      setSelectedCancelTransfer(value);
      setIsCancelTransferModalOpen(true);

      return;
    }
    setSelectedCancelOfferOrder(value);
    setIsCancelOfferOrderModalOpen(true);
  };

  const onViewDetails = (value: Transfer | OfferOrder | Order) => {
    setSelectedHistoryItem(value);
    setIsViewHistoryDetailsModalOpen(true);
  };

  const onConfirmCancelOfferOrder = (selectedItem: OfferOrder) => {
    dispatch(
      doCancelOfferOrder({
        params: {
          id: selectedItem.id,
          accountId: selectedItem.accountId,
        },
      }),
    );
  };

  const onConfirmCancelOrder = (selectedItem: Order) => {
    dispatch(doCancelOrder({ params: { id: selectedItem.id, accountId: account.accountId } }));
  };

  const onChangeOrder = (order: Order) => {
    dispatch(doClearUpdateOrder());
    setSelectedChangeOrder(order);
  };

  const onCloseChangeOrder = () => {
    setSelectedChangeOrder(null);
    dispatch(doClearUpdateOrder());
    dispatch(doClearGetQuote());
    dispatch(doClearGetQuoteChart());
  };

  const onConfirmCancelTransfer = (selectedItem: Transfer) => {
    if (selectedItem.direction.isDeposit) {
      dispatch(cancelDeposit(selectedItem.id, account.accountId));

      return;
    }
    dispatch(cancelWithdraw(selectedItem.id, account.accountId));
  };

  const mapOfferOrderListToViewModelList = (): HistoryViewModel[] =>
    offerOrderList.map((offerOrder: OfferOrder) => ({
      key: offerOrder.id,
      date: offerOrder.updatedAt ?? offerOrder.createdAt,
      amount: <CurrencyField value={-offerOrder.value} className={Styles.amountValue({ isNegative: true })} />,
      category: FilterBarConstant.Show.OfferOrder,
      statusCategory: FilterBarConstant.StatusCategory.OfferOrder,
      status: offerOrder.status.label,
      type: (
        <OfferOrderTypeCol value={offerOrder} onViewMore={() => onViewMore(offerOrder)} testId={'column-type-id'} />
      ),
      change: null,
      cancel: <CancelOfferOrderButton value={offerOrder} onClick={onCancel} />,
      view: <ViewDetailsButton onClick={() => onViewDetails(offerOrder)} />,
    }));

  const mapOrderListToViewModelList = (): HistoryViewModel[] =>
    cumulativeOrderList.map((order: Order) => ({
      key: order.id,
      date: order.updatedAt,
      amount: (
        <CurrencyField value={order.totalCost} className={Styles.amountValue({ isNegative: order.action.isBuy })} />
      ),
      category: order.action.isBuy ? FilterBarConstant.Show.BuyOrder : FilterBarConstant.Show.SellOrder,
      statusCategory: FilterBarConstant.StatusCategory.Order,
      status: order.status.label,
      type: <OrderTypeCol value={order} testId={'column-type-id'} />,
      change: <ChangeOrderButton value={order} onClick={onChangeOrder} />,
      cancel: <CancelOrderButton value={order} onClick={onCancel} />,
      view: <ViewDetailsButton onClick={() => onViewDetails(order)} />,
    }));

  const mapTransferListToViewModelList = (): HistoryViewModel[] =>
    Array.isArray(transferList)
      ? transferList.map((transfer: Transfer) => ({
          key: transfer.id,
          date: transfer.updatedAt,
          amount: (
            <CurrencyField
              value={transfer.direction.isWithdrawal ? -transfer.amount : transfer.amount}
              className={Styles.amountValue({ isNegative: transfer.direction.isWithdrawal })}
            />
          ),
          category: transfer.direction.isDeposit
            ? FilterBarConstant.Show.DepositTransfer
            : FilterBarConstant.Show.WithdrawalTransfer,
          statusCategory: FilterBarConstant.StatusCategory.MoneyTransfer,
          status: transfer.status.label,
          type: <TransferTypeCol value={transfer} testId={'column-type-id'} />,
          change: null,
          cancel: <CancelTransferButton value={transfer} onClick={onCancel} />,
          view: <ViewDetailsButton onClick={() => onViewDetails(transfer)} />,
        }))
      : [];

  const mapOfferOrderStatusToStatusFilterGroup = (): FilterBarConstant.GroupOption[] => {
    const options: FilterBarConstant.GroupOptionItem[] = Object.values(OfferOrderStatusLabel).map(value => ({
      label: <OfferOrderStatusTag value={value} />,
      value: `${value}_${FilterBarConstant.StatusCategory.OfferOrder}`,
      tag: {
        text: value,
        ...getOfferOrderStatusTagColor(value),
      },
    }));

    return [{ title: 'Offer Order(s)', options }];
  };

  const mapOrderStatusToStatusFilterGroup = (): FilterBarConstant.GroupOption[] => {
    const options: FilterBarConstant.GroupOptionItem[] = Object.values(OrderStatusLabel).map(value => ({
      label: <OrderStatusTag value={value} />,
      value: `${value}_${FilterBarConstant.StatusCategory.Order}`,
      tag: {
        text: value,
        ...getOrderStatusTagColor(value),
      },
    }));

    return [{ title: 'Security Order(s)', options }];
  };

  const mapTransferStatusLabelToStatusFilterGroup = (): FilterBarConstant.GroupOption[] => {
    const options: FilterBarConstant.GroupOptionItem[] = Object.values(TransferStatusLabel).map(value => ({
      label: <TransferStatusTag value={value} />,
      value: `${value}_${FilterBarConstant.StatusCategory.MoneyTransfer}`,
      tag: {
        text: value,
        ...getTransferStatusTagColor(value),
      },
    }));

    return [{ title: 'Money Transaction(s)', options }];
  };

  const getStatusFilterGroups = () => {
    let result = mapTransferStatusLabelToStatusFilterGroup();
    result = result.concat(mapOfferOrderStatusToStatusFilterGroup());
    result = result.concat(mapOrderStatusToStatusFilterGroup());

    return result;
  };

  const getShowFilterGroup = (): FilterBarConstant.GroupOption[] => {
    const options = [
      { label: FilterBarConstant.ShowLabel.DepositTransfer, value: FilterBarConstant.Show.DepositTransfer },
      { label: FilterBarConstant.ShowLabel.WithdrawalTransfer, value: FilterBarConstant.Show.WithdrawalTransfer },
      { label: FilterBarConstant.ShowLabel.OfferOrder, value: FilterBarConstant.Show.OfferOrder },
      { label: FilterBarConstant.ShowLabel.BuyOrder, value: FilterBarConstant.Show.BuyOrder },
      { label: FilterBarConstant.ShowLabel.SellOrder, value: FilterBarConstant.Show.SellOrder },
    ];

    return [{ title: 'Show', options }];
  };

  const updateHistoryDataList = () => {
    const transferList: HistoryViewModel[] = mapTransferListToViewModelList();
    const orderHistoryList: HistoryViewModel[] = mapOrderListToViewModelList();
    const offerOrderHistoryList: HistoryViewModel[] = mapOfferOrderListToViewModelList();
    const allRecords = transferList.concat(orderHistoryList, offerOrderHistoryList);
    const orderedAllHistoryList = _.orderBy(allRecords, ['date'], ['desc']);
    setHistoryList(orderedAllHistoryList);
  };

  const onApproveFilters = (newFilters: FilterBarConstant.Sample) => {
    setFilters({ ...newFilters });

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      navigate(`/${NavigatePathConstant.MAIN}/${NavigatePathConstant.HISTORY}`);
    }
  };

  const filterByStatus = (list: HistoryViewModel[]) => {
    const filtersAndCategories = filters.filterBy?.map((filter: CheckboxValueType) => {
      const filterValue = filter.toString();
      const [value, category] = filterValue.split('_');

      return { value, category: category as FilterBarConstant.StatusCategory };
    });

    return list.filter((history: HistoryViewModel) => {
      return (
        filtersAndCategories?.some(({ value, category }) => {
          return category === history.statusCategory && value === history.status;
        }) ||
        !filtersAndCategories?.some(({ category }) => {
          return category === history.statusCategory;
        })
      );
    });
  };

  const applyFilters = (): HistoryViewModel[] => {
    let filteredHistoryList = [...historyList];

    if (filters.show && filters.show.length > 0) {
      filteredHistoryList = filteredHistoryList.filter((history: HistoryViewModel) =>
        filters.show?.includes(history.category),
      );
    }

    if (filters.filterBy && filters.filterBy.length > 0) filteredHistoryList = filterByStatus(filteredHistoryList);

    if (filters.timeline !== undefined) {
      const timelineDate = onFixedTimelineFilterSelected(filters.timeline);
      filteredHistoryList = filteredHistoryList.filter(
        (history: HistoryViewModel) =>
          moment(history.date).isSameOrBefore(timelineDate.endDate) &&
          moment(history.date).isSameOrAfter(timelineDate.startDate),
      );
    }

    if (filters.customTimeline !== undefined) {
      const [startDate, endDate] = filters.customTimeline;
      filteredHistoryList = filteredHistoryList.filter(
        (history: HistoryViewModel) =>
          moment(history.date).isSameOrBefore(endDate) && moment(history.date).isSameOrAfter(startDate),
      );
    }

    filteredHistoryList = _.orderBy(
      filteredHistoryList,
      ['date'],
      [filters.sortBy === FilterBarConstant.SortBy.Newest ? 'desc' : 'asc'],
    );

    return filteredHistoryList;
  };

  useEffect(() => {
    updateHistoryDataList();
  }, [cumulativeOrderList, offerOrderList, transferList, offerDetails]);

  useEffect(() => {
    const historyNewList = applyFilters();
    setFilteredHistory(
      historyNewList.map((item: any) => ({
        ...item,
        date: moment(item.date).format('MM/DD/yyyy'),
      })),
    );
  }, [filters, historyList]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(doClearOfferDetails());
    };
  }, []);

  return (
    <Skeleton loading={isAccountLoading}>
      <Row>
        <Col span={24}>
          <Row justify='center' align='middle' className={Styles.headerContainer}>
            <Col span={24} sm={18} xs={18} className={Styles.titleContainer}>
              <MTypography variant='h4' weight='semibold' color={Color.Brand6}>
                History of Transactions
              </MTypography>
            </Col>
            <Col span={24} sm={6} xs={6} md={24}>
              <FilterBar
                sortByOptions={[
                  { label: FilterBarConstant.SortByLabel.Newest, value: FilterBarConstant.SortBy.Newest },
                  { label: FilterBarConstant.SortByLabel.Oldest, value: FilterBarConstant.SortBy.Oldest },
                ]}
                showFilter={getShowFilterGroup()}
                byFilter={getStatusFilterGroups()}
                onApprove={onApproveFilters}
                defaultValue={defaultValues}
                initValue={filters}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24} className={Styles.tableContainer}>
          <MTable
            columns={historyListCols}
            data={filteredHistory}
            pagination={{ position: ['bottomRight'] }}
            scroll={isMobile ? { x: 180 } : {}}
            loading={isCumulativeOrderListLoading || isTransferListLoading || isOfferOrderListLoading}
            testId={'items-table'}
          />
        </Col>
      </Row>
      <SecuritiesHeldAndFundedOutsideDisclaimerModal
        type={OfferOrderPaymentTypeDto.Card}
        isVisible={isSecuritiesPurchasedByCardModalOpen}
        onClose={() => setIsSecuritiesPurchasedByCardModalOpen(false)}
        onOK={() => setIsSecuritiesPurchasedByCardModalOpen(false)}
      />
      <ExternalOfferDisclaimerModal
        isOpen={isExternalOfferDisclaimerOpen}
        isLoading={isOfferDetailsLoading}
        disclaimer={offerDetails?.externalDetails?.disclaimer}
        onClose={() => {
          setIsExternalOfferDisclaimerOpen(false);
        }}
      />
      {selectedHistoryItem && (
        <HistoryViewDetailsModal
          value={selectedHistoryItem}
          isOpen={isViewHistoryDetailsModalOpen}
          onClose={() => {
            setIsViewHistoryDetailsModalOpen(false);
            setSelectedHistoryItem(null);
          }}
        />
      )}
      {selectedCancelOfferOrder && (
        <ConfirmActionModal
          title='Cancel Confirmation'
          message='Are you sure you want to cancel this offer order?'
          successMessage='Offer order cancellation request was successfully submitted.'
          errorMessage={cancelOfferOrderError?.message}
          isOpen={isCancelOfferOrderModalOpen}
          isError={cancelOfferOrderFailed}
          isSuccess={cancelOfferOrderSuccess}
          isLoading={isCancelOfferOrderLoading}
          onCancel={() => {
            setIsCancelOfferOrderModalOpen(false);
            setSelectedCancelOfferOrder(null);
          }}
          onOk={() => {
            onConfirmCancelOfferOrder(selectedCancelOfferOrder);
          }}
          onClose={() => {
            setIsCancelOfferOrderModalOpen(false);
            setSelectedCancelOfferOrder(null);
            dispatch(doClearCancelOfferOrder());
          }}
        />
      )}
      {selectedCancelOrder && (
        <ConfirmActionModal
          title='Cancel Confirmation'
          message='Are you sure you want to cancel this order?'
          successMessage={`${
            selectedCancelOrder.action.isBuy ? 'Security buy' : 'Security sell'
          } order was successfully cancelled.`}
          errorMessage={cancelOrderError?.message}
          isOpen={isCancelOrderModalOpen}
          isError={cancelOrderFailed}
          isSuccess={cancelOrderSuccess}
          isLoading={isCancelOrderLoading}
          onCancel={() => {
            setIsCancelOrderModalOpen(false);
            setSelectedCancelOrder(null);
          }}
          onOk={() => {
            onConfirmCancelOrder(selectedCancelOrder);
          }}
          onClose={() => {
            setIsCancelOrderModalOpen(false);
            setSelectedCancelOrder(null);
            dispatch(doClearCancelOrder());
          }}
        />
      )}
      {selectedChangeOrder && (
        <ChangeOrderModal open order={selectedChangeOrder} accountId={account.accountId} onClose={onCloseChangeOrder} />
      )}
      {selectedCancelTransfer && (
        <ConfirmActionModal
          title='Cancel Confirmation'
          message={`Are you sure you want to cancel this ${
            selectedCancelTransfer.direction.isDeposit ? 'deposit' : 'withdrawal'
          } transfer?`}
          successMessage={`${
            selectedCancelTransfer.direction.isDeposit ? 'Deposit' : 'Withdrawal'
          } Transfer was successfully cancelled.`}
          errorMessage={
            selectedCancelTransfer.direction.isDeposit ? cancelDepositError?.message : cancelWithdrawError?.message
          }
          isOpen={isCancelTransferModalOpen}
          isError={selectedCancelTransfer.direction.isDeposit ? cancelDepositFailed : cancelWithdrawFailed}
          isSuccess={selectedCancelTransfer.direction.isDeposit ? cancelDepositSuccess : cancelWithdrawSuccess}
          isLoading={selectedCancelTransfer.direction.isDeposit ? isCancelDepositLoading : isCancelWithdrawLoading}
          onCancel={() => {
            setIsCancelTransferModalOpen(false);
            setSelectedCancelTransfer(null);
          }}
          onOk={() => {
            onConfirmCancelTransfer(selectedCancelTransfer);
          }}
          onClose={() => {
            setIsCancelTransferModalOpen(false);
            setSelectedCancelTransfer(null);

            if (selectedCancelTransfer.direction.isDeposit) {
              dispatch(doClearCancelDeposit());

              return;
            }
            dispatch(doClearCancelWithdraw());
          }}
        />
      )}
    </Skeleton>
  );
};

export default History;
